import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysDeliveryNote } from './queryKeys';

type Article = {
  articleNr: string;
  articleName: string;
  articleAmountUnit: string | undefined;
  articleWeightUnit: string | undefined;
  articleEan: number | undefined;
  manufacturerName: string | undefined;
  manufacturerStreet: string | undefined;
  manufacturerHouseNumber: string | undefined;
  manufacturerPostCode: string | undefined;
  manufacturerCity: string | undefined;
  manufacturerCountry: string | undefined;
};

export type ArticleTemplateListItem = {
  id: string;
  companyId: UUID;
  createdById: UUID;
  modifiedById: UUID;
  createdOn: string;
  modifiedOn: string;
  article: Article;
};

export type ArticleTemplatesResponse = {
  articles: ArticleTemplateListItem[];
};

/**
 * Fetches article templates from the API.
 * @param companyId - The IDs of the article templates to fetch.
 * @returns The article templates data.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Article-Master/operation/get_all_from_article_master_for_company_article_master_all_get
 */
export const fetchArticleTemplates = async (companyId: UUID) => {
  const response = await vestigasApi
    .get(ENDPOINT.DELIVERY_NOTE.GET_ALL_ARTICLE_TEMPLATES(), {
      searchParams: { companyId },
    })
    .json<ArticleTemplatesResponse>();

  return response;
};

/**
 * React Query based custom hook for getting the data for a article templates with a given companyId.
 * @param companyId - The companyId.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 */
export const useQueryArticleTemplates = <TSelected = ArticleTemplatesResponse>(
  companyId: UUID,
  options: Omit<
    UseQueryOptions<ArticleTemplatesResponse, Error, TSelected>,
    'queryKey' | 'queryFn'
  > = {},
) => {
  return useQuery<ArticleTemplatesResponse, Error, TSelected>({
    enabled: Boolean(companyId),
    queryFn: async () => fetchArticleTemplates(companyId),
    queryKey: queryKeysDeliveryNote.getAllArticleTemplates({ companyId }),
    ...options,
  });
};
